import TableCol from "@rio-cloud/rio-uikit/TableCol";
import TableHead from "@rio-cloud/rio-uikit/TableHead";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import NoData from "@rio-cloud/rio-uikit/NoData";
import isEmpty from "lodash/fp/isEmpty";
import DummyRowDropdown from "./DummyRowDropdown";
import { ColumnDetailsMap, ColumnLabel } from "./Toolbar";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import { useContext } from "react";
import { CoreContext } from "src/providers/CoreProvider";
import { Spinner } from "@rio-cloud/rio-uikit";

type SortKey<T> = keyof T | [keyof T, keyof T];

const VISIBLE_SORT_KEY: Record<string, string> = {
  sortableDate: "date",
  sortableAmount: "amount",
};

export type Vehicle = {
  id: string;
  date: string;
  sortableDate?: string;
  amount: string;
  sortableAmount?: number;
  branch: string;
  segment: string;
  customer: string;
  vehicle: string;
  driver: string[];
  category: string;
  subcategory: string;
  taxes: string;
  contract: string;
  registration: string;
};

interface Props {
  rows: Vehicle[];
  isViewTypeTable: boolean;
  tableRef: React.RefObject<HTMLTableElement>;
  tableClassNames: string;
  columns: string[];
  columnsDetails: ColumnDetailsMap;
  columnLabels: ColumnLabel;
  sortKey: SortKey<Vehicle>;
  sortDirection: SortDirectionType;
  handleSortChange: (event: React.MouseEvent<HTMLTableCellElement>) => void;
  onActiveRowChange: (event: React.MouseEvent<HTMLTableRowElement>) => void;
  handleDelete: (id: string) => void;
  onDeleteFile?: (id: string) => void;
  onGetFile?: (id: string) => void;
  handleEdit: (id: string) => void;
  handleObservation: (id: string) => void;
  isLoading?: boolean;
}

const Table = ({
  rows,
  isViewTypeTable,
  tableRef,
  tableClassNames,
  columns,
  columnLabels,
  columnsDetails,
  sortKey,
  sortDirection,
  handleSortChange,
  onActiveRowChange,
  handleDelete,
  handleEdit,
  handleObservation,
  onDeleteFile,
  onGetFile,
  isLoading = false,
}: Props) => {
  const { drivers } = useContext(CoreContext);

  const getNameDriver = (id: string) => {
    const find = drivers?.find((res) => res?.id === id);
    return find ? (
      <span className="text-capitalize">
        {find?.first_name} {find?.last_name}
      </span>
    ) : (
      <span />
    );
  };

  if (isLoading) {
    return (
      <div className="display-flex flex-column justify-content-center align-items-center">
        <Spinner isInline={false} text="Carregando..." />
      </div>
    );
  }

  return (
    <div className="table-responsive min-height-50pct" style={{ position: "relative" }}>
      {isEmpty(rows) && !isViewTypeTable && (
        <NotFoundState message="Nenhuma receita cadastrada no período" outerClassName="bg-none" />
      )}
      <table ref={tableRef} className={tableClassNames}>
        <colgroup>
          {columns.map((column) => (
            <TableCol key={column} columnDetails={columnsDetails[column]} />
          ))}
          <TableCol className="table-action" />
        </colgroup>
        <thead>
          <tr>
            {columns.map((column) => {
              const sortKeyString = typeof sortKey === "string" ? sortKey : sortKey[0];
              const displaySortKey = VISIBLE_SORT_KEY[sortKeyString] || sortKeyString;

              return (
                <TableHead
                  key={column}
                  label={columnLabels[column]}
                  column={column}
                  sortBy={displaySortKey}
                  sortDir={sortDirection}
                  onClick={handleSortChange}
                />
              );
            })}
            <TableHead className="table-action" />
          </tr>
        </thead>
        <tbody>
          {isEmpty(rows) && isViewTypeTable && (
            <tr className="bg-none">
              <td colSpan={columns.length + 1}>
                <NotFoundState outerClassName="border-none bg-none" message="Nenhuma receita cadastrada no período" />
              </td>
            </tr>
          )}
          {rows.map((row, index) => {
            return (
              <tr key={`${row.id}-${index}`} data-id={row.date} onClick={onActiveRowChange} className="cursor-pointer">
                {columns.map((col) => {
                  const item = row[col as keyof Vehicle] ?? <NoData text="-" />;

                  return (
                    <td key={col} data-field={columnLabels[col]}>
                      {col === "driver" ? (
                        row.driver?.map((re) => {
                          return (
                            <span key={re}>
                              {getNameDriver(re)}
                              <br />
                            </span>
                          );
                        })
                      ) : (
                        <span>{item}</span>
                      )}
                    </td>
                  );
                })}
                <td className="table-action">
                  <DummyRowDropdown
                    items={[
                      // {
                      //     icon: "rioglyph rioglyph-new-window",
                      //     onClick: () => console.log(row.date),
                      //     title: 'Ver comprovantes'
                      // },
                      {
                        icon: "rioglyph rioglyph-download",
                        onClick: () => onGetFile?.(row.id),
                        title: "Baixar comprovante",
                      },
                      {
                        icon: "rioglyph rioglyph-trash ",
                        onClick: () => onDeleteFile?.(row.id),
                        title: "Excluir comprovante",
                      },
                      {
                        icon: "rioglyph rioglyph-info-sign",
                        onClick: () => handleObservation(row.id),
                        title: "Ver observações",
                      },
                      {
                        icon: "rioglyph rioglyph-pencil",
                        onClick: () => handleEdit(row.id),
                        title: "Editar",
                      },

                      {
                        icon: "rioglyph rioglyph-trash",
                        onClick: () => handleDelete(row.id),
                        title: "Excluir",
                      },
                    ]}
                  />
                </td>
              </tr>
            );
          })}
          {/* Placeholder workaround for equal with cards of the last row */}
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
          <tr className="table-card-placeholder" />
        </tbody>
      </table>
    </div>
  );
};

export default Table;
